import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSlideScreen } from "@/shared/hooks/useSlideScreen";
import { Chip } from "@a2z/ui";
import { imageUrl } from "@a2z/ui/media/image-url";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getBuyerAbsoluteUrl, getSellerPath } from "@/lib/routing/portal-urls";
import { formatCurrency } from "@/lib/formatting/currency";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { ProductEditSections } from "@/modules/products/components/ProductEditSections";
import { ProductDeleteButton } from "@/modules/products/components/ProductDeleteButton";
import { ProductCheckoutLinkSheet } from "@/modules/products/components/ProductCheckoutLinkSheet";
import { ShareMenu } from "@/shared/components/ShareMenu";
import { ProductSummaryCard } from "@/modules/products/components/ProductSummaryCard";
import { useCategoriesQuery } from "@/shared/services/useCategoriesQuery";
import { useProductByIdQuery } from "@/modules/products/services/useProductByIdQuery";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { PageTransition } from "@/shared/components/PageTransition";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

type ProductEditPageProps = {
  locale: Locale;
  storeSlug: string;
  productId: string;
};

function parseMoney(value: string | null): number | null {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function ProductEditPage({
  locale,
  storeSlug,
  productId,
}: ProductEditPageProps) {
  const t = useTranslations();
  const { data: product, isLoading: isProductLoading, error: productError } =
    useProductByIdQuery({
      storeSlug,
      productId,
    });
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } =
    useCategoriesQuery({ storeSlug });
  const {
    data: storeData,
    isLoading: isStoreLoading,
    error: storeError,
  } = useStoreQuery({ storeSlug });
  const productErrorStatus = getQueryErrorStatus(productError);

  // Register the edit-product title into the shared chrome slot.
  const titleLabel = t(keys.seller.productsEditTitle);
  const backLabel = t(keys.common.back);
  const previewLabel = t(keys.products.editSummaryPreview);
  const productsHref = `${getSellerPath(locale, storeSlug)}/products`;
  // Primitives read by the mobile hero (kept as stable strings so the hero
  // effect never depends on the whole `product` object — see the aux note below).
  const heroProductRef = product?.productRef ?? "";
  const heroSubtitle = pickLocalized(locale, product?.nameAr ?? "", product?.nameEn ?? null);
  const heroPreviewHref = heroProductRef
    ? getBuyerAbsoluteUrl(
        locale,
        storeSlug,
        `/products/${encodeURIComponent(heroProductRef)}`,
      )
    : null;
  const { setHero, setHeroAux, setBreadcrumb } = usePageChrome();
  const isMobile = useIsMobile();
  const unsavedLabel = t(keys.common.unsavedChanges);
  const [isDirty, setIsDirty] = useState(false);
  const handleDirtyChange = useCallback((next: boolean) => {
    setIsDirty(next);
  }, []);
  // Mobile-only hero (there is no desktop top bar on phones). On desktop the
  // breadcrumb lives in the top bar (set below), so this is hidden at lg+.
  //
  // On mobile the header is now a *sticky* element rendered inside
  // ProductEditSections (it pins while the cards scroll, matching the Trioty
  // design) — so we null the PageChrome hero here to avoid a duplicate,
  // scroll-away header in the (non-sticky) HeroRegion.
  useEffect(() => {
    if (isMobile) {
      setHero(null);
      return () => setHero(null);
    }
    setHero(
      <div className="flex min-w-0 items-center gap-2.5 lg:hidden">
        <Link
          to={productsHref}
          aria-label={backLabel}
          className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
        >
          <BackIcon />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <h1 className="m-0 truncate text-[1.125rem] font-extrabold leading-tight tracking-tight text-[color:var(--text-primary)]">
            {titleLabel}
          </h1>
          {heroSubtitle ? (
            <span className="truncate text-[12px] font-semibold text-[color:var(--text-muted)]">
              {heroSubtitle}
            </span>
          ) : null}
        </div>
        {isDirty ? (
          <Chip
            tone="accent"
            size="sm"
            className="shrink-0 !bg-[color-mix(in_oklab,var(--accent-500)_15%,transparent)] !text-[color:var(--accent-500)] !border-[color-mix(in_oklab,var(--accent-500)_35%,transparent)]"
            leadingIcon={
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--accent-500)]" />
            }
          >
            {unsavedLabel}
          </Chip>
        ) : null}
        {heroPreviewHref ? (
          <a
            href={heroPreviewHref}
            target="_blank"
            rel="noreferrer"
            aria-label={previewLabel}
            className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))]"
          >
            <EyeIcon />
          </a>
        ) : null}
      </div>,
    );
    return () => setHero(null);
  }, [
    isMobile,
    setHero,
    titleLabel,
    backLabel,
    productsHref,
    isDirty,
    unsavedLabel,
    heroSubtitle,
    heroPreviewHref,
    previewLabel,
  ]);

  // Extract the primitives needed to render the hero summary card.
  // We intentionally avoid depending on the whole `product` object —
  // React Query can return a new product reference on any background
  // revalidation, which would make a whole-object memo re-emit and fire
  // the setHeroAux effect every render, cascading through PageChromeContext
  // back into this component and looping.
  const hiddenReasonsKey =
    (product?.sellerBuyerHiddenReasons ?? []).slice().sort().join(",");
  const defaultVariantFromList =
    product?.variants.find((v) => v.id === product.defaultVariantId) ??
    product?.variants.find((v) => v.isDefault) ??
    product?.variants[0] ??
    null;
  const auxDefaultVariantId = defaultVariantFromList?.id ?? null;
  const auxStockCount = defaultVariantFromList?.stockCount ?? 0;
  const auxDefaultVariantPrice = defaultVariantFromList?.price ?? 0;
  const auxDefaultVariantDiscounted =
    defaultVariantFromList?.discountedPrice ?? null;
  const auxPricingMode = product?.pricingSummary.mode ?? "single";
  const auxPricingMin = product?.pricingSummary.currentMin ?? null;
  const auxPricingMax = product?.pricingSummary.currentMax ?? null;
  const auxHasPurchasable =
    product?.availabilitySummary.hasPurchasableVariant ?? false;
  const auxFirstReadyImage =
    product?.images?.find((img) => img.status === "READY") ?? null;
  const auxFirstReadyImageUrl = auxFirstReadyImage?.objectKey
    ? imageUrl(
        auxFirstReadyImage.objectKey,
        {
          width: 480,
          format: "auto",
          removeBackground: auxFirstReadyImage.useBgRemoved === true,
        },
        { baseUrl: MEDIA_BASE_URL },
      )
    : null;
  const auxProductId = product?.id ?? null;
  const auxProductRef = product?.productRef ?? null;
  const auxNameEn = product?.nameEn ?? null;
  const auxNameAr = product?.nameAr ?? "";

  // Desktop top-bar breadcrumb: "📦 Products › {product name}" — the active
  // product (the "package") sits as the trailing crumb. Falls back to the
  // generic title while the product name loads.
  const crumbName = pickLocalized(locale, auxNameAr, auxNameEn) || titleLabel;
  const productsLabel = t(keys.seller.productsTitle);
  useEffect(() => {
    setBreadcrumb(
      <nav
        aria-label={productsLabel}
        className="flex min-w-0 items-center gap-1.5"
      >
        <Link
          to={productsHref}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-1.5 py-1 font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))] hover:text-[color:var(--text-primary)]"
        >
          <BreadcrumbBoxIcon />
          <span>{productsLabel}</span>
        </Link>
        <ChevronSepIcon />
        <span className="truncate font-semibold text-[color:var(--text-primary)]">
          {crumbName}
        </span>
        {isDirty ? (
          <Chip
            tone="accent"
            size="sm"
            className="ms-1 shrink-0 !border-[color-mix(in_oklab,var(--accent-500)_35%,transparent)] !bg-[color-mix(in_oklab,var(--accent-500)_15%,transparent)] !text-[color:var(--accent-500)]"
            leadingIcon={
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--accent-500)]" />
            }
          >
            {unsavedLabel}
          </Chip>
        ) : null}
      </nav>,
    );
    return () => setBreadcrumb(null);
  }, [
    setBreadcrumb,
    productsHref,
    productsLabel,
    crumbName,
    isDirty,
    unsavedLabel,
  ]);

  // Cache the variants array reference keyed on its composition so the
  // share-sheet trigger doesn't re-render unnecessarily. The refs are synced
  // in an effect (not during render — react-hooks/refs); it is declared
  // before the hero-aux effect below so the sync always runs first, and the
  // ref is only read inside that effect.
  const variantsKey = product?.variants.map((v) => v.id).join(",") ?? "";
  const variantsRef = useRef(product?.variants);
  const lastVariantsKeyRef = useRef(variantsKey);
  const latestVariants = product?.variants;
  useEffect(() => {
    if (lastVariantsKeyRef.current !== variantsKey) {
      lastVariantsKeyRef.current = variantsKey;
      variantsRef.current = latestVariants;
    }
  }, [variantsKey, latestVariants]);

  useEffect(() => {
    // The Trioty mobile edit design has no summary card — Preview lives in the
    // sticky header and Share lives on the product detail view. Drop it on
    // mobile; keep it on desktop (right-rail / hero aux) unchanged.
    if (isMobile || !auxProductId) {
      setHeroAux(null);
      return;
    }
    const fallbackCurrentPrice =
      auxDefaultVariantDiscounted ?? auxDefaultVariantPrice ?? 0;
    const currentMin = parseMoney(auxPricingMin) ?? fallbackCurrentPrice;
    const currentMax = parseMoney(auxPricingMax);
    const isRange =
      auxPricingMode === "range" && currentMax !== null && currentMax > currentMin;
    const priceLabel = isRange
      ? `${formatCurrency(currentMin, locale)} - ${formatCurrency(currentMax, locale)}`
      : formatCurrency(currentMin, locale);
    const identityStatus: "active" | "hidden" | "out_of_stock" =
      hiddenReasonsKey.includes("product_inactive") ||
      hiddenReasonsKey.includes("missing_images")
        ? "hidden"
        : !auxHasPurchasable
          ? "out_of_stock"
          : "active";
    const productName = pickLocalized(locale, auxNameAr, auxNameEn);
    setHeroAux(
      <ProductSummaryCard
        productName={productName}
        productRef={auxProductRef ?? ""}
        status={identityStatus}
        stockCount={auxStockCount}
        priceLabel={priceLabel}
        firstImageUrl={auxFirstReadyImageUrl ?? undefined}
        shareButton={
          <div className="flex items-center gap-2">
            {/* Share lives here (product details) — moved off the list cards. */}
            <ShareMenu
              url={getBuyerAbsoluteUrl(
                locale,
                storeSlug,
                `/products/${encodeURIComponent(auxProductRef ?? "")}`,
              )}
              messageKey={keys.products.shareMessage}
              messageValuesEn={{ name: auxNameEn ?? auxNameAr }}
              messageValuesAr={{ name: auxNameAr }}
              title={t(keys.products.shareTitle)}
              triggerAriaLabel={t(keys.products.shareAria)}
            />
            <ProductCheckoutLinkSheet
              storeSlug={storeSlug}
              productId={auxProductId}
              variants={variantsRef.current ?? []}
              variant="onHero"
            />
          </div>
        }
      />,
    );
  }, [
    t,
    auxProductId,
    auxProductRef,
    auxNameEn,
    auxNameAr,
    auxDefaultVariantId,
    auxStockCount,
    auxDefaultVariantPrice,
    auxDefaultVariantDiscounted,
    auxPricingMode,
    auxPricingMin,
    auxPricingMax,
    auxHasPurchasable,
    auxFirstReadyImageUrl,
    hiddenReasonsKey,
    variantsKey,
    locale,
    setHeroAux,
    storeSlug,
    isMobile,
  ]);

  // Mobile back → return to the product detail VIEW (not the list), with a
  // reverse slide-out. The detail is URL-driven (`?product=<id>` on the list).
  const navigate = useNavigate();
  const backToViewHref = `${productsHref}?product=${productId}`;
  const editSlide = useSlideScreen<HTMLDivElement>(() =>
    navigate(backToViewHref, { state: { fromEdit: true } }),
  );

  if (
    isProductLoading ||
    (isCategoriesLoading && !categoriesData) ||
    (isStoreLoading && !storeData)
  ) {
    return (
      <PageTransition className="mt-6 flex w-full flex-col gap-4 sm:mt-8">
        <div className="text-sm text-[color:var(--text-muted)]">
          {t(keys.common.loading)}
        </div>
      </PageTransition>
    );
  }

  if (productErrorStatus === 404) {
    return (
      <PageTransition className="mt-6 flex w-full flex-col gap-4 sm:mt-8">
        <div className="text-sm text-[color:var(--text-muted)]">
          {t(keys.errors.notFound)}
        </div>
      </PageTransition>
    );
  }

  if (productError || categoriesError || storeError || !product) {
    return (
      <PageTransition className="mt-6 flex w-full flex-col gap-4 sm:mt-8">
        <div className="text-sm text-[color:var(--text-muted)]">
          {t(keys.errors.generic)}
        </div>
      </PageTransition>
    );
  }

  const maxProductImageCount = storeData?.maxProductImageCount ?? 3;

  const body = (
    <>
      <ProductEditSections
        locale={locale}
        storeSlug={storeSlug}
        categories={categoriesData ?? []}
        product={product}
        maxProductImageCount={maxProductImageCount}
        onDirtyChange={handleDirtyChange}
        onBack={editSlide.close}
      />
      <div className="flex w-full flex-wrap items-center justify-between gap-4 px-4 pt-1 max-lg:flex-col max-lg:items-stretch max-lg:gap-2 max-lg:pb-3 lg:px-7 lg:pb-8">
        <p className="max-w-md text-[12.5px] text-[color:var(--text-muted)] max-lg:hidden">
          {t(keys.products.deleteProductFooterHint)}
        </p>
        <ProductDeleteButton
          locale={locale}
          storeSlug={storeSlug}
          productId={product.id}
        />
      </div>
    </>
  );

  // Mobile: slide in like the product detail view; the sticky header's back
  // button plays the reverse slide-out then returns to the detail view. Desktop
  // keeps the standard PageTransition.
  if (isMobile) {
    return (
      <div ref={editSlide.ref} className="flex w-full flex-col">
        {body}
      </div>
    );
  }

  return <PageTransition className="flex w-full flex-col">{body}</PageTransition>;
}

function BackIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:-scale-x-100"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function BreadcrumbBoxIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </svg>
  );
}

function ChevronSepIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[color:var(--text-disabled,var(--text-muted))] rtl:rotate-180"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
